import type { DeviceContext } from '../types/events'

/**
 * Detect device information from user agent and browser APIs
 */
export function getDeviceContext(): DeviceContext {
  const ua = navigator.userAgent

  return {
    type: getDeviceType(ua),
    os: getOS(ua),
    os_version: getOSVersion(ua),
    browser: getBrowser(ua),
    browser_version: getBrowserVersion(ua),
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }
}

function getDeviceType(ua: string): 'desktop' | 'mobile' | 'tablet' {
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet'
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile'
  }
  return 'desktop'
}

function getOS(ua: string): string {
  if (/Windows/.test(ua)) return 'Windows'
  if (/Mac OS X/.test(ua)) return 'macOS'
  if (/Linux/.test(ua)) return 'Linux'
  if (/Android/.test(ua)) return 'Android'
  if (/iOS|iPhone|iPad|iPod/.test(ua)) return 'iOS'
  return 'Unknown'
}

function getOSVersion(ua: string): string {
  const match = ua.match(/(?:Windows NT|Mac OS X|Android|iOS) ([\d._]+)/)
  return match ? match[1].replace(/_/g, '.') : 'Unknown'
}

function getBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return 'Edge'
  if (/Chrome/.test(ua) && !/Edg/.test(ua)) return 'Chrome'
  if (/Safari/.test(ua) && !/Chrome/.test(ua)) return 'Safari'
  if (/Firefox/.test(ua)) return 'Firefox'
  if (/MSIE|Trident/.test(ua)) return 'Internet Explorer'
  return 'Unknown'
}

function getBrowserVersion(ua: string): string {
  const browser = getBrowser(ua)
  let match: RegExpMatchArray | null = null

  switch (browser) {
    case 'Chrome':
      match = ua.match(/Chrome\/([\d.]+)/)
      break
    case 'Edge':
      match = ua.match(/Edg\/([\d.]+)/)
      break
    case 'Safari':
      match = ua.match(/Version\/([\d.]+)/)
      break
    case 'Firefox':
      match = ua.match(/Firefox\/([\d.]+)/)
      break
  }

  return match ? match[1] : 'Unknown'
}

/**
 * Get page context information
 */
export function getPageContext() {
  const url = new URL(window.location.href)
  const queryParams: Record<string, string> = {}

  url.searchParams.forEach((value, key) => {
    queryParams[key] = value
  })

  return {
    url: window.location.href,
    path: window.location.pathname,
    title: document.title,
    referrer: document.referrer,
    query_params: queryParams
  }
}